'use client';

import { startTransition, useState } from 'react';
import type { SimpleNewsQuery } from '@benzinga/internal-news-manager';

import { ArticleNewsFeed } from './newsfeed/ArticleNewsFeed';
import { TradeIdeasBadge, type BadgeProps } from './newsfeed/TradeIdeasBadge';
import { DashboardPillTabs } from '../../../edge/dashboard/_components/DashboardPillTabs';
import { PortfolioSectionHeader } from './PortfolioSectionHeader';

const categories: {
  label: string;
  query: SimpleNewsQuery;
  badge?: BadgeProps;
}[] = [
  { label: 'All', query: {} },
  {
    label: 'Earnings',
    query: { channels: ['Earnings'] },
    badge: {
      title: 'Earnings',
      description: 'Follow earnings news to get the latest information on how a company is performing.',
      button: 'Unlock Blazing Fast News',
    },
  },
  {
    label: 'Analyst',
    query: { channels: ['Analyst Ratings'] },
    badge: {
      title: 'Analyst Ratings',
      description:
        'Follow the latest analyst ratings to get a professional opinion on how a stock may perform. Analysts will do extensive research into a company to come up with a rating, and provide a suggestion on how to trade a stock.',
      button: 'Unlock Analyst Ratings',
    },
  },
  {
    label: 'Insiders',
    query: { channels: ['Insider Trades'] },
    badge: {
      title: 'Insider Trades',
      description:
        "Seeing how insiders trade their own stocks can indicate internal sentiments of a company's projected performance. Keep an eye out for when high ranking directors sell/purchase shares, as this could indicate a move in the share price.",
      button: 'Unlock Insider Trades',
    },
  },
  {
    label: 'Ideas',
    query: { channels: ['Trading Ideas'] },
    badge: {
      title: 'Trading Ideas',
      description: 'Get trade ideas from the leading experts to find new strategies and ways to trade.',
      button: 'Unlock Blazing Fast News',
    },
  },
];

const fallbackBadge = categories.find(category => category.badge)?.badge;

export const WatchlistNewsSection = ({ bodyHeight = 420 }: { bodyHeight?: number }) => {
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-slate-200">
        <PortfolioSectionHeader
          bordered={false}
          title="Watchlist News"
          titleAddon={
            fallbackBadge ? (
              <div
                className={`transition-opacity duration-150 ease-out ${
                  selectedCategory.badge ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
                aria-hidden={!selectedCategory.badge}
              >
                <TradeIdeasBadge badge={selectedCategory.badge ?? fallbackBadge} />
              </div>
            ) : null
          }
        />
        <div className="px-3 pb-2">
          <DashboardPillTabs
            activeTab={selectedCategory.label}
            density="compact"
            onChange={label => {
              const nextCategory = categories.find(category => category.label === label);
              if (nextCategory) {
                startTransition(() => setSelectedCategory(nextCategory));
              }
            }}
            tabs={categories.map(category => ({ key: category.label, name: category.label }))}
          />
        </div>
      </div>
      <div className="overflow-y-auto px-2 py-1.5 sm:px-3" style={{ height: bodyHeight }}>
        <ArticleNewsFeed
          title={selectedCategory.label}
          newsQuery={selectedCategory.query}
          showTimeAgo
          embedded
          compact
          hideTitle
          skeletonCount={8}
        />
      </div>
    </div>
  );
};
